import * as HoverCard from "@radix-ui/react-hover-card";
import { Flex, Text, Button } from "@radix-ui/themes";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FaChevronRight } from "react-icons/fa";

import { marketplaceTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import FilterCheckbox from "./shared/FilterCheckbox";
import FilterFooter from "./shared/FilterFooter";
import useFilterDropdown from "./shared/useFilterDropdown";
import {
  KEY_SIGNATURE_OPTIONS,
  getCanonicalKeyValue,
} from "./shared/keySignatures";
import { MarketplaceFilterKey, MarketplaceUpdates } from "../types";

type KeyFilterProps = {
  parameter?: string;
  onSetFilter: (updates: MarketplaceUpdates) => void;
  onRemoveFilter: (param: MarketplaceFilterKey) => void;
};

export default function KeyFilter({
  parameter,
  onSetFilter,
  onRemoveFilter,
}: KeyFilterProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const parameterKey = useMemo(
    () => getCanonicalKeyValue(parameter),
    [parameter],
  );

  const restoreFromParameter = useCallback(() => {
    setSelectedKey(parameterKey);
  }, [parameterKey]);

  const {
    open,
    isPinned,
    handleOpenChange,
    handleTriggerClick,
    handleInteractOutside,
    close,
  } = useFilterDropdown({ id: "key", onRestore: restoreFromParameter });

  useEffect(() => {
    if (!open) {
      setSelectedKey(parameterKey);
    }
  }, [parameterKey, open]);

  function toggleKey(signature: string) {
    setSelectedKey((prev) => (prev === signature ? null : signature));
  }

  function handleApply() {
    if (!selectedKey) {
      onRemoveFilter("key");
      close({ restore: false });
      return;
    }

    if (selectedKey !== parameterKey) {
      onSetFilter({ key: selectedKey });
    }

    close({ restore: false });
  }

  function handleReset() {
    setSelectedKey(null);
    onRemoveFilter("key");
    close({ restore: false });
  }

  const hasSelection = Boolean(parameterKey);

  function renderKeyButton({ value, label }: { value: string; label: string }) {
    const isSelected = selectedKey === value;
    return (
      <button
        key={value}
        type="button"
        onClick={() => toggleKey(value)}
        role="radio"
        aria-checked={isSelected}
        aria-label={label}
        className={cn(
          "h-[34px] px-2 rounded flex items-center gap-2 text-left cursor-pointer transition-colors flex-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 text-white hover:bg-[#3A404D]/60 hover:text-teal-500",
          {
            "bg-[#3A404D]/60": isSelected,
          },
        )}
        style={{
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <span className="w-4 h-4 shrink-0 relative">
          <FilterCheckbox checked={isSelected} />
        </span>
        <Text
          className={cn("text-[14px] font-semibold", {
            "text-teal-500": isSelected,
          })}
        >
          {label}
        </Text>
      </button>
    );
  }

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={handleOpenChange}
      openDelay={isPinned ? 0 : 200}
      closeDelay={isPinned ? 0 : 100}
    >
      <HoverCard.Trigger asChild>
        <Button
          onClick={handleTriggerClick}
          className={cn(
            "flex items-center gap-[8px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black",
            marketplaceTokens.filterItem,
            hasSelection && "!text-white !bg-background-neutral-inverted",
            {
              [marketplaceTokens.filterItemActive]: open,
              [marketplaceTokens.filterItemInactive]: !open,
            },
          )}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-pressed={isPinned}
        >
          <Text className="flex-1">Key</Text>
          <FaChevronRight
            size={10}
            aria-hidden
            className={cn("transition-transform", {
              "rotate-90": open,
            })}
          />
        </Button>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="bottom"
          avoidCollisions={false}
          align="start"
          sideOffset={2}
          className="w-[460px] bg-background-neutral-inverted rounded shadow-[0px_10px_18px_-1px_rgba(5,12,43,0.14)] pt-4 px-4 pb-3 flex flex-col gap-4 z-50"
          onInteractOutside={handleInteractOutside}
        >
          <Flex direction="column" className="gap-4">
            <div role="radiogroup" aria-label="Key">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <Text className="text-[12px] tracking-wide text-gray-500 font-semibold">
                  Major
                </Text>
                <Text className="text-[12px] tracking-wide text-gray-500 font-semibold">
                  Minor
                </Text>
                {KEY_SIGNATURE_OPTIONS.map((option) => (
                  <Fragment key={option.id}>
                    {renderKeyButton({
                      value: option.major.value,
                      label: `${option.display} Major`,
                    })}
                    {renderKeyButton({
                      value: option.minor.value,
                      label: `${option.display} Minor`,
                    })}
                  </Fragment>
                ))}
              </div>
            </div>
          </Flex>

          <FilterFooter
            onReset={handleReset}
            onApply={handleApply}
            variant="bordered"
          />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
